import { ModalConfirmCreateHierarchy } from 'components/modal/ModalConfirmCreateHierarchy';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSalesChannelList } from 'services/queryClient/wrapperHooks/salesChannel/useSalesChannelList';
import { useGetFilterCommercialHierarchy } from 'services/queryClient/wrapperHooks/useGetFilterCommercialHierarchy';
import { useSalesForceList } from 'services/queryClient/wrapperHooks/useSalesForceList';

import { useCreateSale } from '../../services/queryClient/wrapperHooks/useCreateSale';
import { useEditSale } from '../../services/queryClient/wrapperHooks/useEditSale';
import { useSalesZoneList } from '../../services/queryClient/wrapperHooks/useSalesZoneList';
import {
	renderClient,
	renderFamily,
	renderProduct,
} from '../../utils/forms/autocomplete';
import { makeGenericFormInputStructure } from '../../utils/forms/defaultInputs';
import {
	columnFlexProps,
	defaultFormStyle,
	defaultInputStyle,
	formFlexProps,
	rowFlexProps,
} from '../../utils/forms/defaultsProps';
import {
	validateByRegex,
	validateMin,
	validateRequired,
} from '../../utils/forms/validations';
import { currencyToNumber } from '../../utils/functions/currencyToNumber';
import { filterNotInformedOptions } from '../../utils/functions/filterNotInformedOptions';
import { Loading360 } from '../loading/Loading360';
import GenericForm, { IFormRows } from './GenericForm';

interface IFormCadastrarEditarVendas {
	initialState: any;
	saleId?: number;
	autocompleteAdditionalData?: any;
}

const FormCadastrarEditarVendas = ({
	initialState,
	saleId = 0,
	autocompleteAdditionalData,
}: IFormCadastrarEditarVendas) => {
	const history = useHistory();

	const isEditing = !!saleId;

	const { data: SalesChannelData, isLoading: isLoadingSalesChannel } =
		useSalesChannelList();
	const { data: SalesZoneData, isLoading: IsLoadingSalesZone } =
		useSalesZoneList();
	const {
		data: CommercialHierarchyData,
		isLoading: isLoadingCommercialHierarchy,
	} = useGetFilterCommercialHierarchy(undefined);
	const { data: SalesForceData, isLoading: isLoadingSalesForce } =
		useSalesForceList();

	const [familyId, setFamilyId] = useState<number>();
	const [productId, setProductId] = useState<number>();
	const [forceFamilyValue, setForceFamilyValue] = useState<any>(null);
	const [forceSubFamilyValue, setForceSubFamilyValue] = useState<any>(null);
	const [forceModelValue, setForceModelValue] = useState<any>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pendingPayload, setPendingPayload] = useState<any>(null);

	const handleOpenModal = useCallback(() => {
		setIsModalOpen(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		setPendingPayload(null);
	}, []);

	const { mutate: createSale } = useCreateSale(handleOpenModal);
	const { mutate: updateSale } = useEditSale(saleId, handleOpenModal);

	const handleConfirmCreateHierarchy = useCallback(() => {
		if (pendingPayload) {
			const bodyWithCreate = {
				...pendingPayload,
				is_create_hierarchy: true,
			};
			if (isEditing) {
				updateSale(bodyWithCreate);
			} else {
				createSale(bodyWithCreate);
			}
		}
		setIsModalOpen(false);
	}, [pendingPayload, isEditing, createSale, updateSale]);

	useEffect(() => {
		setFamilyId(initialState?.familia_id);
		setProductId(initialState?.produto_id);
	}, [initialState]);

	const goToVendas = useCallback(() => history.goBack(), [history]);

	const getMaxDate = useMemo(
		() => moment().startOf('day').format('YYYY-MM-DD'),
		[]
	);

	const channelOptions = useMemo(() => {
		const filteredChannels = filterNotInformedOptions(
			SalesChannelData || [],
			'nome_canal_venda'
		);

		return filteredChannels.map(s => (
			<option
				key={s?.id}
				value={s?.id}
			>
				{s?.nome_canal_venda}
			</option>
		));
	}, [SalesChannelData]);

	const zoneOptions = useMemo(() => {
		const filteredZones = filterNotInformedOptions(
			SalesZoneData?.data || [],
			'nome_zona_venda'
		);

		return filteredZones.map(s => (
			<option
				key={s?.id}
				value={s?.id}
			>
				{s?.nome_zona_venda}
			</option>
		));
	}, [SalesZoneData]);

	const marketOptions = useMemo(() => {
		const filteredMarkets = filterNotInformedOptions(
			CommercialHierarchyData?.mercados || [],
			'nome_mercado'
		);

		return filteredMarkets.map(m => (
			<option
				key={m?.id}
				value={m?.id}
			>
				{m?.nome_mercado}
			</option>
		));
	}, [CommercialHierarchyData]);

	const salesForceOptions = useMemo(() => {
		const filteredSalesForce = filterNotInformedOptions(
			SalesForceData?.data || [],
			'nome_forca_venda'
		);

		return filteredSalesForce.map(sf => (
			<option
				key={sf?.id}
				value={sf?.id}
			>
				{sf?.nome_forca_venda}
			</option>
		));
	}, [SalesForceData]);

	const memberOptions = useMemo(() => {
		const filteredMembers = filterNotInformedOptions(
			CommercialHierarchyData?.membros || [],
			'nome_membro'
		);

		return filteredMembers.map(m => (
			<option
				key={m?.id}
				value={m?.id}
			>
				{m?.nome_membro}
			</option>
		));
	}, [CommercialHierarchyData]);

	const handleOnChangeChannel = useCallback(() => {}, []);

	const handleOnChangeFamily = useCallback(
		(value, formProps) => {
			const newFamilyId = value?.id;

			if (productId && newFamilyId !== familyId) {
				setProductId(undefined);
				if (formProps) {
					formProps.setValue('produto_id', null);
				}
			}

			setFamilyId(newFamilyId);
		},
		[productId, familyId]
	);

	const handleOnChangeProduct = useCallback((value, formProps) => {
		setProductId(value?.id);

		if (formProps) {
			const { setValue } = formProps;

			if (value?.familia_id && value?.nome_familia) {
				const familyData = {
					id: value.familia_id,
					nome_familia: value.nome_familia,
				};

				setValue('familia_id', value.familia_id);
				setFamilyId(value.familia_id);
				setForceFamilyValue(familyData);
				setTimeout(() => {
					setForceFamilyValue(null);
				}, 50);
			}

			if (value?.sub_familia_id && value?.nome_subfamilia) {
				const subFamilyData = {
					id: value.sub_familia_id,
					nome_sub_familia: value.nome_subfamilia,
				};

				setValue('sub_familia_id', value.sub_familia_id);
				setForceSubFamilyValue(subFamilyData);
				setTimeout(() => {
					setForceSubFamilyValue(null);
				}, 50);
			}

			if (value?.modelo_produto_id && value?.nome_modelo) {
				const modelData = {
					id: value.modelo_produto_id,
					nome_modelo: value.nome_modelo,
				};

				setValue('modelo_produto_id', value.modelo_produto_id);
				setForceModelValue(modelData);
				setTimeout(() => {
					setForceModelValue(null);
				}, 50);
			}
		}
	}, []);

	const handleOnChangeNF = useCallback((event, form) => {
		form.setValue('sequencia_nf', '');
	}, []);

	const isDisabledProductAutocomplete = useCallback(() => {
		return false;
	}, []);

	const shouldClearAutocompleteProduct = useCallback(() => {
		if (!productId && familyId) {
			return true;
		}
		return false;
	}, [familyId, productId]);

	const onSubmit = useCallback(
		values => {
			const body = {
				data_venda:
					moment(values?.data_venda, 'YYYY-MM-DD').toISOString() ||
					null,
				nf: values?.nf || null,
				sequencia_nf: values?.sequencia_nf,
				codigo_pedido: values?.codigo_pedido || null,
				canal_venda_id: values?.canal_venda_id
					? Number(values?.canal_venda_id)
					: null,
				zona_venda_id: values?.zona_venda_id
					? Number(values?.zona_venda_id)
					: null,
				mercado_id: values?.mercado_id
					? Number(values?.mercado_id)
					: null,
				forca_venda_id: values?.forca_venda_id
					? Number(values?.forca_venda_id)
					: null,
				membro_id: values?.membro_id ? Number(values?.membro_id) : null,
				cliente_id: values?.cliente_id || null,
				familia_id: values?.familia_id || null,
				sub_familia_id: values?.sub_familia_id
					? Number(values?.sub_familia_id)
					: null,
				modelo_produto_id: values?.modelo_produto_id
					? Number(values?.modelo_produto_id)
					: null,
				produto_id: values?.produto_id || null,
				quantidade: values?.quantidade
					? currencyToNumber(values?.quantidade)
					: null,
				receita_bruta: values?.receita_bruta
					? currencyToNumber(values?.receita_bruta)
					: null,
			};

			setPendingPayload(body);

			if (isEditing) updateSale(body);
			else createSale(body);
		},
		[createSale, isEditing, updateSale]
	);

	const renderRows = useCallback((): IFormRows[] => {
		return [
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'text',
							name: 'nf',
							label: 'NF',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							inputProps: {
								'data-test':
									'input-nf-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								onChange: handleOnChangeNF,
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'number',
							name: 'sequencia_nf',
							label: 'Sequência da Nota Fiscal',
							validate: {
								...validateMin(
									1,
									'O valor deve ser maior ou igual a 1!'
								),
								...validateByRegex(
									/^[1-9]\d*$/,
									'O valor deve ser um número inteiro válido!'
								),
							},
							inputProps: {
								'data-test':
									'input-sequencia_da_nota_fiscal-cadastrar_editar-page-vendas',
								...defaultInputStyle,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'date',
							name: 'data_venda',
							label: 'Data da NF',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							inputProps: {
								'data-test':
									'input-data_da_nf-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								max: getMaxDate,
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'text',
							name: 'codigo_pedido',
							label: 'Código do Pedido',
							inputProps: {
								'data-test':
									'input-codigo_pedido-cadastrar_editar-page-vendas',
								...defaultInputStyle,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'cliente_id',
							label: 'Razão Social',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							inputProps: {
								'data-test':
									'autocomplete-razao_social-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								searchType: 'cliente',
								renderInputLabel: renderClient,
								autocomplete: 'off',
								initialLabel: autocompleteAdditionalData?.client
									? renderClient(
											autocompleteAdditionalData?.client
									  )
									: '',
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'familia_id',
							label: 'Família',
							inputProps: {
								'data-test':
									'autocomplete-familia-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								searchType: 'familia',
								renderInputLabel: renderFamily,
								autocomplete: 'off',
								initialLabel: autocompleteAdditionalData?.family
									? renderFamily(
											autocompleteAdditionalData?.family
									  )
									: '',
								onChange: handleOnChangeFamily,
								forceValue: forceFamilyValue,
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'produto_id',
							label: 'Produto',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							disabled: isDisabledProductAutocomplete,
							inputProps: {
								'data-test':
									'autocomplete-produto-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								searchType: 'produto',
								renderInputLabel: renderProduct,
								autocomplete: 'off',
								initialLabel:
									autocompleteAdditionalData?.product
										? renderProduct(
												autocompleteAdditionalData?.product
										  )
										: '',
								extraRequestData: familyId ? { familyId } : {},
								clearInput: shouldClearAutocompleteProduct,
								onChange: handleOnChangeProduct,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'product-quantity',
							name: 'quantidade',
							label: 'Quantidade',
							showRequiredOnLabel: true,
							validate: {
								...validateRequired(),
							},
							inputProps: {
								'data-test':
									'input-quantidade-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								allowNegativeValue: true,
								prefix: '',
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'decimal',
							name: 'receita_bruta',
							label: 'Receita Bruta',
							showRequiredOnLabel: true,
							validate: {
								...validateRequired(),
							},
							inputProps: {
								'data-test':
									'input-receita_bruta-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								allowNegativeValue: true,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'mercado_id',
							label: 'Mercado',
							inputProps: {
								'data-test':
									'select-mercado-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								selectOptions: marketOptions,
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'canal_venda_id',
							label: 'Canal de Venda',
							inputProps: {
								'data-test':
									'select-canal_de_venda-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								selectOptions: channelOptions,
								onChange: handleOnChangeChannel,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'forca_venda_id',
							label: 'Força de Venda',
							inputProps: {
								'data-test':
									'select-forca_venda-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								selectOptions: salesForceOptions,
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'zona_venda_id',
							label: 'Zona de Venda',
							inputProps: {
								'data-test':
									'select-zona_de_venda-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								selectOptions: zoneOptions,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'membro_id',
							label: 'Membro',
							inputProps: {
								'data-test':
									'select-membro-cadastrar_editar-page-vendas',
								...defaultInputStyle,
								selectOptions: memberOptions,
							},
						}),
					},
				],
			},
		];
	}, [
		handleOnChangeNF,
		getMaxDate,
		autocompleteAdditionalData?.client,
		autocompleteAdditionalData?.product,
		autocompleteAdditionalData?.family,
		isDisabledProductAutocomplete,
		shouldClearAutocompleteProduct,
		handleOnChangeProduct,
		handleOnChangeFamily,
		forceFamilyValue,
		forceSubFamilyValue,
		forceModelValue,
		familyId,
		autocompleteAdditionalData?.sub_family,
		autocompleteAdditionalData?.product_model,
		marketOptions,
		channelOptions,
		handleOnChangeChannel,
		salesForceOptions,
		zoneOptions,
		memberOptions,
	]);

	const renderForm = useCallback(() => {
		if (
			isLoadingSalesChannel ||
			IsLoadingSalesZone ||
			isLoadingCommercialHierarchy ||
			isLoadingSalesForce
		)
			return (
				<Loading360
					size={48}
					variant='gradient'
					table={true}
					message={true}
				/>
			);

		return (
			<>
				<GenericForm
					dataTestProps={{
						cancel: 'button-cancelar-page-vendas',
						save: 'button-salvar-page-vendas',
					}}
					initialState={initialState}
					formFlexProps={formFlexProps}
					rows={renderRows()}
					style={defaultFormStyle}
					onSubmit={onSubmit}
					onCancel={goToVendas}
				/>
				<ModalConfirmCreateHierarchy
					isOpen={isModalOpen}
					onConfirm={handleConfirmCreateHierarchy}
					onCancel={handleCloseModal}
				/>
			</>
		);
	}, [
		initialState,
		onSubmit,
		renderRows,
		goToVendas,
		isLoadingSalesChannel,
		IsLoadingSalesZone,
		isLoadingCommercialHierarchy,
		isLoadingSalesForce,
		isModalOpen,
		handleCloseModal,
		handleConfirmCreateHierarchy,
	]);

	return renderForm();
};

export default FormCadastrarEditarVendas;
